// Admin "Bank accounts" — route /bank-accounts (deliberately not
// "/admin/bank-accounts": any client path starting with "/admin" collides
// with the dev proxy's / Nginx's API prefix on a real navigation, see
// App.jsx's route-table comment and AdminNav.jsx's header comment).
//
// The accounts here are what the proforma and the payment-request document
// print in their banking block — this screen is what unblocks that template.
// Scope: list, create, edit, and soft-disable (deactivate / reactivate via
// the PATCH endpoint's isActive flag — no hard delete route exists for this
// table, matching the users screen). is_active doubles as "prints on the
// proforma"; sort_order is the order it prints in.
//
// GET /admin/bank-accounts returns inactive rows too — they stay listed,
// dimmed, same as the users screen; an inactive row still offers Reactivate.

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useBankAccounts,
  useCreateBankAccount,
  useUpdateBankAccount,
} from '../hooks/useBankAccounts.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import BankAccountForm from '../components/BankAccountForm.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './BankAccountsPage.module.css';

const KNOWN_CODES = ['BANK_ACCOUNT_NOT_FOUND', 'VALIDATION_ERROR'];

function BankAccountRow({ account, onEdit }) {
  const { t } = useLanguage();
  const updateMutation = useUpdateBankAccount();
  const { confirm, toastOk, toastErr } = useActionFeedback();

  const handleToggleActive = async () => {
    if (account.is_active) {
      const ok = await confirm({
        title: t('bankAccounts.actions.deactivateConfirmTitle', { name: account.bank_name }),
        message: t('bankAccounts.actions.deactivateConfirmBody'),
        confirmLabel: t('bankAccounts.actions.deactivateConfirm'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    updateMutation.mutate(
      { id: account.id, patch: { isActive: !account.is_active } },
      {
        onSuccess: () =>
          toastOk(
            account.is_active
              ? t('bankAccounts.deactivatedToast')
              : t('bankAccounts.reactivatedToast')
          ),
        onError: (err) => toastErr(err, KNOWN_CODES),
      }
    );
  };

  return (
    <tr className={`${styles.row} ${!account.is_active ? styles.rowInactive : ''}`}>
      <td className="tabularNums">{account.sort_order}</td>
      <td>{account.bank_name}</td>
      <td>{account.account_name}</td>
      <td className="tabularNums">{account.account_number}</td>
      <td>{account.branch || t('bankAccounts.notSet')}</td>
      <td className="tabularNums">{account.swift || t('bankAccounts.notSet')}</td>
      <td>
        <span className={account.is_active ? styles.statusActive : styles.statusInactive}>
          {t(account.is_active ? 'bankAccounts.status.onProforma' : 'bankAccounts.status.hidden')}
        </span>
      </td>
      <td className={styles.actionsCell}>
        <button type="button" className={styles.linkButton} onClick={() => onEdit(account.id)}>
          {t('bankAccounts.actions.edit')}
        </button>
        <button
          type="button"
          className={account.is_active ? styles.deactivateLink : styles.linkButton}
          onClick={handleToggleActive}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending && <Spinner />}{' '}
          {account.is_active
            ? t('bankAccounts.actions.deactivate')
            : t('bankAccounts.actions.reactivate')}
        </button>
      </td>
    </tr>
  );
}

export default function BankAccountsPage() {
  const { t } = useLanguage();
  // null | 'create' | <id> — only one form panel open at a time.
  const [formMode, setFormMode] = useState(null);
  const { toastOk, toastErr } = useActionFeedback();

  const { data: accounts, isLoading, isError, error, refetch } = useBankAccounts();
  const createMutation = useCreateBankAccount();
  const updateMutation = useUpdateBankAccount();

  const allAccounts = accounts || [];
  const isTrulyEmpty = !isLoading && !isError && allAccounts.length === 0;
  const editing = typeof formMode === 'number' ? allAccounts.find((a) => a.id === formMode) : null;

  const handleCreate = (body) => {
    createMutation.mutate(body, {
      onSuccess: () => {
        setFormMode(null);
        toastOk(t('bankAccounts.createdToast'));
      },
      onError: (err) => toastErr(err, KNOWN_CODES),
    });
  };

  const handleEdit = (body) => {
    updateMutation.mutate(
      { id: formMode, patch: body },
      {
        onSuccess: () => {
          setFormMode(null);
          toastOk(t('bankAccounts.updatedToast'));
        },
        onError: (err) => toastErr(err, KNOWN_CODES),
      }
    );
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('bankAccounts.title')}</h1>
      <p className={styles.subtitle}>{t('bankAccounts.subtitle')}</p>

      <div className={styles.filterRow}>
        <button
          type="button"
          className={styles.newButton}
          onClick={() => setFormMode((prev) => (prev === 'create' ? null : 'create'))}
        >
          {t('bankAccounts.newAccount')}
        </button>
      </div>

      {formMode === 'create' && (
        <BankAccountForm
          onSubmit={handleCreate}
          onCancel={() => setFormMode(null)}
          isPending={createMutation.isPending}
        />
      )}
      {editing && (
        <BankAccountForm
          key={editing.id}
          initialValues={editing}
          onSubmit={handleEdit}
          onCancel={() => setFormMode(null)}
          isPending={updateMutation.isPending}
        />
      )}

      {isLoading && <LoadingState variant="rows" rows={4} />}

      {isError && !isLoading && (
        <ErrorState messageId="bankAccounts.errorBody" error={error} onRetry={refetch} />
      )}

      {isTrulyEmpty && (
        <EmptyState titleId="bankAccounts.emptyTitle" bodyId="bankAccounts.emptyBody" />
      )}

      {!isLoading && !isError && allAccounts.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th>{t('bankAccounts.col.order')}</th>
              <th>{t('bankAccounts.col.bankName')}</th>
              <th>{t('bankAccounts.col.accountName')}</th>
              <th>{t('bankAccounts.col.accountNumber')}</th>
              <th>{t('bankAccounts.col.branch')}</th>
              <th>{t('bankAccounts.col.swift')}</th>
              <th>{t('bankAccounts.col.status')}</th>
              <th>{t('bankAccounts.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {allAccounts.map((account) => (
              <BankAccountRow key={account.id} account={account} onEdit={setFormMode} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
